import React, { useState, useEffect } from 'react';
import useExternalIntegrations from 'App/components/hooks/integrations/useExternalIntegrations';
import UiWidget from 'App/components/UiWidget/UiWidget';
import type { Widget } from 'App/components/hooks/integrations/externalIntegrationTypes';
import type { EditOrderOrder, EditOrderClient } from '../types';

import './ExternalWidgetsSidebar.scss';
import BemClass from '@upsales/components/Utils/bemClass';
import { Flex, Loader, Text } from '@upsales/components';
import logError from 'Helpers/logError';
import { Order, FieldActions } from 'App/resources/Model/Order';
import { useForceRender } from 'App/components/hooks';
import { default as ClientModel } from 'App/resources/Model/Client';
import useDataSources from 'App/components/UiWidget/useDataSources';
import ErrorBoundary from 'App/components/ErrorBoundary';
import T from 'Components/Helpers/translate';

interface ExternalWidgetsSidebarProps {
	type: 'order_sidebar';
	entityId: number;
	entity: Order | EditOrderOrder;
	client: ClientModel | EditOrderClient | null;
	updateOrderFromApp: (fieldActions: FieldActions, order?: Order | EditOrderOrder) => void;
}

type EntityWithClient = (Omit<Order, 'client'> | Omit<EditOrderOrder, 'client'>) & { client: ClientModel };

interface WidgetWithDataSourceProps {
	widget: Widget;
	updateOrderFromApp: (fieldActions: FieldActions, order?: Order | EditOrderOrder) => void;
	entityDataSource: Record<'order', EntityWithClient | undefined>;
}

const WidgetWithDataSource = ({ widget, updateOrderFromApp, entityDataSource }: WidgetWithDataSourceProps) => {
	const { dataSources, settings } = useDataSources(widget.externalId, widget.settings?.dataSource);

	return (
		<UiWidget
			widget={widget}
			updateOrderFromApp={updateOrderFromApp}
			dataSources={{ ...dataSources, ...entityDataSource }}
			settings={settings}
		/>
	);
};

const ExternalWidgetsSidebar = ({
	entityId,
	entity,
	type,
	client,
	updateOrderFromApp
}: ExternalWidgetsSidebarProps) => {
	const classes = new BemClass('ExternalWidgetsSidebar');
	const { getWidgetsByType } = useExternalIntegrations();
	const [widgets, setWidgets] = useState<Widget[]>([]);
	const [activeWidgets, setActiveWidgets] = useState<Widget[]>([]);
	const [loading, setLoading] = useState(true);
	const { triggerRender } = useForceRender();

	const [entityWithClient, setEntityWithClient] = useState<EntityWithClient>();

	useEffect(() => {
		if (client && entity) {
			setEntityWithClient({
				...entity,
				client
			} as EntityWithClient);
		}
	}, [client, entity]);

	const fetchWidgetsByType = async () => {
		try {
			const widgets = await getWidgetsByType(type);
			setWidgets(widgets);
		} catch (error) {
			logError(error, `Error fetching widgets by type: ${type}`);
			setActiveWidgets([]);
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		const widgetsHaveChanged = widgets.length !== activeWidgets.length;
		if (entityWithClient || widgetsHaveChanged) {
			const widgetsWithTranslatedDataSources = widgets.map(widget => {
				if (!widget?.settings?.dataSource) {
					return widget;
				}
				if (widget.settings.dataSource.order?.upsalesId === '{{order.id}}') {
					if (entity) {
						widget.settings.dataSource.order.upsalesEntity = entityWithClient as Order;
					} else if (entityId) {
						widget.settings.dataSource.order.upsalesId = `${entityId}`;
					}
				}
				return widget;
			});
			setActiveWidgets(widgetsWithTranslatedDataSources);
			triggerRender();
		}
	}, [entityWithClient, widgets]);

	useEffect(() => {
		fetchWidgetsByType();
	}, []);

	return (
		<Flex className={classes.b()} space="mtxl ptm" direction="column">
			{loading ? <Loader /> : null}
			{activeWidgets.map((widget: Widget) => (
				<ErrorBoundary
					key={widget.id}
					fallback={() => (
						<Text color="red" size="sm">
							{T('default.couldNotLoadWidget')}
						</Text>
					)}
				>
					<WidgetWithDataSource
						widget={widget}
						updateOrderFromApp={updateOrderFromApp as any}
						entityDataSource={{ ['order']: entityWithClient }}
					/>
				</ErrorBoundary>
			))}
		</Flex>
	);
};

export default ExternalWidgetsSidebar;
